<script setup lang="ts">
//======================================================================//
//                           IMPORTS                                    //
//======================================================================//
import { inject, watch, ref, computed } from "vue"
import { useRoute } from "vue-router"
import { storeToRefs } from "pinia"
import type { Services } from "@services"
import { METER_TYPES_LIST, SOURCES_TYPES } from "@core/constants"
import { useI18n } from "vue-i18n"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"

//======================================================================//
//                     Composables & Inject                             //
//======================================================================//

const services = inject<Services>("services")!
const { $createGraphByBuildingId } = services
const route = useRoute()
const buildingStore = useBuildingStore()
const { building, isFetchingBuilding, isFetchingBuildingSources } =
    storeToRefs(buildingStore)

/**
 * Indique si le bâtiment ou ses sources sont en cours de chargement.
 */
const isFetching = computed(
    () => isFetchingBuilding.value || isFetchingBuildingSources.value
)

//======================================================================//
//                              Data                                    //
//======================================================================//

const graph = ref<{ data: object[]; layout: object; config: object } | null>(
    null
)
const freq = ref<"hourly" | "daily">("hourly")

/** Modal d’ajout de compteur PDL */
const sourceAppendModalVisible = ref(false)

//======================================================================//
//                           i18n                                       //
//======================================================================//

const { t } = useI18n({
    messages: {
        fr: {
            title: "Profil d'utilisation",
            error_no_pdl:
                "Pour afficher le graphique de consommation, ajoutez au moins un compteur électrique (PDL) au bâtiment.",
            add_pdl: "Ajouter un compteur électrique (PDL)",
            graph_error: "Impossible de charger le graphique.",
            retry: "Réessayer",
            freq_hourly: "Par heure",
            freq_daily: "Par jour",
            title_graph: "Profil de consommation",
        },
    },
})

//======================================================================//
//                         Helpers                                      //
//======================================================================//

/**
 * Retourne la date du jour au format YYYY-MM-DD.
 */
function getTodayISO(): string {
    const d = new Date()
    return d.toISOString().split("T")[0]
}

/**
 * Retourne la date du jour moins 1 an au format YYYY-MM-DD.
 */
function getOneYearAgoISO(): string {
    const d = new Date()
    d.setFullYear(d.getFullYear() - 1)
    return d.toISOString().split("T")[0]
}

//======================================================================//
//                     Computed                                         //
//======================================================================//

/**
 * True si le bâtiment a au moins une source de type PDL.
 */
const getHasPdl = computed(() => {
    const src = building.value?.sources ?? []
    return src.some((s: { idtype?: string }) => s.idtype === SOURCES_TYPES.PDL)
})

//======================================================================//
//                     Fetch graph                                      //
//======================================================================//

/**
 * Appelle l'API pour récupérer le graphique profil_weekly et met à jour graph.
 */
const {
    fetch: fetchGraph,
    isFetching: isFetchingGraph,
    hasError: hasErrorGraph,
    error: errorGraph,
    cancel: cancelGraphFetch,
} = useFetchWrapper(async (_kwargs?: unknown, controller?: AbortController) => {
    const id = building.value?._id
    if (!id || !getHasPdl.value) return
    graph.value = null
    const date_from = getOneYearAgoISO()
    const date_to = getTodayISO()
    const datasets = [
        {
            type: "energie_elec_tout.sum",
            graph_type: "profil_weekly",
            unit: "kWh",
            name:
                freq.value === "hourly"
                    ? "Profil hebdomadaire par heure"
                    : "Profil hebdomadaire par jour",
        },
    ]
    const response = await $createGraphByBuildingId(
        id,
        date_from,
        date_to,
        freq.value,
        datasets,
        {
            ylabel: "Consommation (kWh)",
            abordController: controller,
        }
    )
    const parsed = JSON.parse(response.data) as {
        data: object[]
        layout: object
        config: object
    }
    graph.value = parsed
})

//======================================================================//
//                     Event handlers                                   //
//======================================================================//

/**
 * Ouvre la modal d’ajout PDL.
 */
function onAddPdl() {
    sourceAppendModalVisible.value = true
}

/**
 * Fin du parcours PDL : même rechargement que succès.
 */
async function onSourceAppendDone(): Promise<void> {
    const id = route.params._id_building as string | undefined
    if (!id) return
    await buildingStore.fetchBuilding(id, { silent: true })
    await buildingStore.fetchBuildingSources({
        sourceTypes: METER_TYPES_LIST,
        force: true,
        silent: true,
    })
    if (getHasPdl.value) await fetchGraph()
}

/**
 * Recharge le graphique (retry).
 */
function onRetryGraph() {
    fetchGraph()
}

//======================================================================//
//                     Watchers & Lifecycle                             //
//======================================================================//
watch(
    [building, getHasPdl, freq],
    () => {
        if (building.value && getHasPdl.value) {
            fetchGraph()
        } else {
            graph.value = null
            cancelGraphFetch()
        }
    },
    { immediate: true }
)
</script>

<template>
    <div class="page page-building-profil-utilisation">
        <HeaderPageBuilding :title="t('title')" />
        <BaseMessage
            v-if="buildingStore.hasErrorBuilding"
            icon="pi pi-exclamation-triangle"
            severity="error"
            class="p-4"
        >
            {{ buildingStore.errorBuilding?.message }}
        </BaseMessage>
        <div v-else-if="isFetching">
            <div v-for="index in 2" :key="index">
                <BaseSkeleton class="mb-4" width="100%" height="40rem" />
            </div>
        </div>
        <div v-else class="flex flex-col gap-8">
            <UsageProfileCard />

            <BaseTitledCard
                header-class="flex flex-col sm:flex-row gap-2 sm:justify-between sm:items-center"
            >
                <template #header>
                    <h2 class="text-lg font-semibold text-primary flex gap-x-3">
                        {{ t("title_graph") }}
                    </h2>
                    <BaseSelectButton
                        class="ml-auto"
                        v-model="freq"
                        :options="[
                            { label: t('freq_hourly'), value: 'hourly' },
                            { label: t('freq_daily'), value: 'daily' },
                        ]"
                        option-label="label"
                        option-value="value"
                    />
                </template>
                <template #content>
                    <div class="relative" v-if="getHasPdl">
                        <BaseChartSkeleton
                            v-if="isFetchingGraph || !graph"
                            class="h-80 w-full"
                        />
                        <PlotlyChart
                            v-else
                            :graph="graph"
                            class="min-h-80 w-full"
                        />
                        <BaseMessage
                            v-if="hasErrorGraph"
                            severity="error"
                            icon="pi pi-times-circle"
                            :closable="false"
                            class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                        >
                            {{ errorGraph?.message ?? t("graph_error") }}
                            <BaseButton
                                class="mt-2"
                                size="small"
                                :label="t('retry')"
                                @click="onRetryGraph"
                            />
                        </BaseMessage>
                    </div>
                    <template v-else>
                        <BaseMessage
                            icon="pi pi-info-circle"
                            severity="warn"
                            class="max-w-xl mx-auto"
                        >
                            <div class="flex flex-col items-center gap-2">
                                <p>{{ t("error_no_pdl") }}</p>
                                <BaseButton
                                    :label="t('add_pdl')"
                                    size="small"
                                    severity="warn"
                                    @click="onAddPdl"
                                />
                            </div>
                        </BaseMessage>
                    </template>
                </template>
            </BaseTitledCard>
        </div>

        <SourceAppendDialog
            v-model:visible="sourceAppendModalVisible"
            from-state="pdl"
            :allowed-source-types="['pdl']"
            :building-id="(route.params._id_building as string) ?? undefined"
            :address="building?.address ?? null"
            @done="onSourceAppendDone"
        />
    </div>
</template>
